import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import React, { useContext, useEffect, useState } from "react";
import { Button } from "rizzui/button";
import { Text } from "rizzui/typography";
import { inputClassName, labelClassName } from "../modules/projects/new/first";
import { ThemeContext } from "../context/themeContext";
import axios from "axios";
import useFetchData from "../hooks/useFetchData";
import { AuthContext } from "../context/authContext";
import { baseUrl } from "../modules/account/login";
import { useNavigate } from "react-router";
import { useModal } from "../hooks/useModal";
import { useNotifcication } from "../hooks/useNotification";
import { useSaveCard } from "../hooks/usePayments";
import { useQueryClient } from "@tanstack/react-query";

const AddCardForm = () => {
  const { closeModal, view } = useModal();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const AuthStr = "Bearer ".concat(token);
  const { openNotification } = useNotifcication();
  const { mutate: saveCard, status } = useSaveCard();
  const queryClient = useQueryClient();

  const fetchClientSecret = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/payments/stripe/get_client_secret`,
        {
          headers: { Authorization: AuthStr },
        }
      );
      return response.data;
    } catch (e) {
      if (e?.response?.data?.message === "Session expired") {
        navigate("/account/logout");
      }
      throw new Error(e?.message ?? "Check your internet connection");
    }
  };

  const handleClear = () => {
    const cardNumberElement = elements?.getElement(CardNumberElement);
    const cardExpiryElement = elements?.getElement(CardExpiryElement);
    const cardCvcElement = elements?.getElement(CardCvcElement);
    if (cardNumberElement) {
      cardNumberElement.clear();
      cardCvcElement?.clear();
      cardExpiryElement?.clear();
    }
  };

  useEffect(() => {
    handleClear();
  }, [view]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const card = elements!.getElement(CardNumberElement)!;

    try {
      const clientSecret = await fetchClientSecret();
      const secret = clientSecret?.data?.client_secret;
      const { setupIntent, error } = await stripe!.confirmCardSetup(
        secret, // <-- resolved clientSecret from backend
        {
          payment_method: {
            card,
          },
        }
      );

      if (error) {
        console.error(error);
        setLoading(false);
        openNotification({
          status: "error",
          message: error?.message ?? "Check your internet connection",
        });
      } else {
        console.log(setupIntent);
        saveCard(setupIntent, {
          onSuccess: () => {
            setLoading(false);
            queryClient.invalidateQueries({
              queryKey: ["user_cards", token],
            });
            closeModal();
            openNotification({
              status: "success",
              message: "Card added successfully",
            });
          },
          onError: (error) => {
            setLoading(false);
            openNotification({
              status: "error",
              message: "Error adding card",
            });
          },
        });
      }
    } catch (error) {
      setLoading(false);
      openNotification({
        status: "error",
        message: error?.message ?? "Check your internet connection",
      });
      console.error(error);
    }
  };

  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const ELEMENT_OPTIONS = {
    style: {
      base: {
        fontSize: "12px",
        color: isDark ? "white" : "#191919",
        fontFamily: "Arial, sans-serif",
        "::placeholder": {
          color: "#a0aec0",
        },
      },
      invalid: {
        color: "#e53e3e",
      },
    },
  };

  return (
    <div className="w-[96%] md:w-[471px] flex flex-col gap-[28px] mx-auto bg-background dark:bg-[#2A2C30] p-[32px] rounded-[8px] shadow-lg">
      <button
        className="absolute top-4 right-4 p-1 rounded-lg z-10 text-gray-400 bg-transparent hover:text-gray-600 dark:hover:text-gray-300"
        onClick={closeModal}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="size-[18px]"
        >
          <path
            fillRule="evenodd"
            d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      <div className="flex flex-col gap-[8px]">
        <Text className="font-ibmPlexSerif font-[600] text-[18px] text-primary-dark">
          Add your card
        </Text>
        <Text className="font-[400] text-[12px] text-[#758599] font-switzer">
          We’ll charge your card only when you want to add credit to your wallet
        </Text>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-[28px]">
        <div className="flex flex-col gap-[8px]">
          <label className={labelClassName}>Card number</label>
          <div className="border border-muted rounded-[4px] p-2 h-[35px] items-center flex">
            <CardNumberElement options={ELEMENT_OPTIONS} className="w-full" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-[16px]">
          <div className="flex flex-col gap-[8px]">
            <label className={labelClassName}>Expiry</label>
            <div className="border border-muted rounded-[4px] p-2 h-[35px] items-center flex">
              <CardExpiryElement options={ELEMENT_OPTIONS} className="w-full" />
            </div>
          </div>

          <div className="flex flex-col gap-[8px]">
            <label className={labelClassName}>CVV</label>
            <div className="border border-muted rounded-[4px] p-2 h-[35px] items-center flex">
              <CardCvcElement options={ELEMENT_OPTIONS} className="w-full" />
            </div>
          </div>
        </div>
        <Button
          className="h-[40px] w-full rounded-[4px] px-[32px] mx-auto bg-btn hover:!bg-btn font-[500] font-switzer text-[12px] leading-[15.84px] text-background"
          type="submit"
          isLoading={loading}
        >
          Add your card
        </Button>
      </form>
      <Text className="font-[400] font-switzer text-[12px] text-[#758599] text-center">
        Your card will be stored securely in stripe
      </Text>
    </div>
  );
};

export default AddCardForm;
